import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { type FC, type RefObject, useRef } from "react";
import { DashboardCardValue } from "web_myplann/Dashboard/Components/DashboardCardValue";
import { DashboardCarouselDots } from "web_myplann/Dashboard/Components/DashboardCarouselDots";
import { DashboardGradientLayer } from "web_myplann/Dashboard/Components/DashboardGradientLayer";
import { EvaluationDelta } from "web_myplann/Dashboard/Components/EvaluationDelta";
import { useGradientCrossFade } from "web_myplann/Dashboard/Hooks/useGradientCrossFade";
import type { DashboardType } from "web_myplann/Dashboard/Types/DashboardType";
import { DASHBOARD_DETAIL_HEADER_CONTENT_HEIGHT } from "web_myplann/Dashboard/Utils/dashboardLayout";
import { getDashboardLinearGradient } from "web_myplann/Dashboard/Utils/getDashboardGradient";
import { getDashboardValueState } from "web_myplann/Dashboard/Utils/getDashboardValueState";
import {
	type SwipeableComponentRef,
	useSwipeable,
} from "web_myplann/Shared/Hooks/useSwipeable";

type Props = {
	heroRef: RefObject<HTMLDivElement | null>;
	dashboard: DashboardType;
	dashboardCount: number;
	activeIndex: number;
	onDashboardChange: (index: number) => void;
};

export const DashboardDetailHeader: FC<Props> = ({
	heroRef,
	dashboard,
	dashboardCount,
	activeIndex,
	onDashboardChange,
}) => {
	const { palette, spacingTokens, navigation } = useTheme();
	const dotsRef = useRef<SwipeableComponentRef>(null);

	const { handlePointerDown, handlePointerMove, handlePointerEnd } =
		useSwipeable(dotsRef, false);

	const { slots, frontSlot } = useGradientCrossFade(
		getDashboardLinearGradient(dashboard.gradientFrom, dashboard.gradientTo),
	);

	const { isMissingValueWithItems, evaluation } =
		getDashboardValueState(dashboard);

	return (
		<Stack ref={heroRef} position="relative" sx={{ background: slots[0] }}>
			<DashboardGradientLayer gradient={slots[1]} isVisible={frontSlot === 1} />
			<Stack
				position="relative"
				alignItems="center"
				px={spacingTokens.lg}
				pt={`${navigation.header.height}px`}
				pb={spacingTokens.lg}
				sx={{ userSelect: "none", touchAction: "pan-y" }}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerEnd}
				onPointerCancel={handlePointerEnd}
			>
				<Stack
					alignItems="center"
					justifyContent="center"
					height={DASHBOARD_DETAIL_HEADER_CONTENT_HEIGHT}
				>
					<Typography
						variant="bodyMedium"
						color={alpha(palette.darkmodeText.white, 0.8)}
						mb={spacingTokens.xs}
					>
						{dashboard.type}
					</Typography>
					<DashboardCardValue
						dashboardData={dashboard}
						isMissingValueWithItems={isMissingValueWithItems}
					/>
					{evaluation !== undefined && (
						<Box mt={spacingTokens.md}>
							<EvaluationDelta evaluation={evaluation} />
						</Box>
					)}
				</Stack>

				{dashboardCount > 1 && (
					<Box mt={spacingTokens.lg}>
						<DashboardCarouselDots
							ref={dotsRef}
							length={dashboardCount}
							currentValue={activeIndex}
							onClick={onDashboardChange}
						/>
					</Box>
				)}
			</Stack>
		</Stack>
	);
};
